/**
 * 회원전용 알림톡 플로우
 *
 * 회원 전용 캠페인에서 비로그인 유저에게 보여주는 안내 UI.
 * 전화번호 SMS 인증 후 알림톡(회원가입 안내)을 발송한다.
 * 이미 회원인 경우 로그인 버튼으로 바로 이동 가능하다.
 */

import { useState } from 'react'
import { initPixel, trackPixelEvent } from '@/lib/metaPixel'
import { sendCapiEvent } from '@/lib/metaCapi'
import { hashPhone } from '@/lib/metaHash'
import type { PublicCampaignResponse } from '@/lib/seedingPublicApi'
import { seedingPublicApi } from '@/lib/seedingPublicApi'
import { formatPhoneInput, stripPhone } from '@/lib/phoneUtils'
import { getCookie } from './utils'

interface MemberOnlySignupFlowProps {
  campaign: PublicCampaignResponse
  mallId: string
  shopNo: number
  campaignId: string
  onLogin: () => void
}

export function MemberOnlySignupFlow({
  campaign,
  mallId,
  shopNo,
  campaignId,
  onLogin,
}: MemberOnlySignupFlowProps) {
  const [phone, setPhone] = useState('')
  const [phoneVerified, setPhoneVerified] = useState(false)
  const [codeSent, setCodeSent] = useState(false)
  const [verificationCode, setVerificationCode] = useState('')
  const [isSendingCode, setIsSendingCode] = useState(false)
  const [isVerifyingCode, setIsVerifyingCode] = useState(false)
  const [isSendingNotify, setIsSendingNotify] = useState(false)
  const [notifySent, setNotifySent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // 인증코드 발송
  const handleSendCode = async () => {
    if (!phone) {
      setError('전화번호를 입력해주세요.')
      return
    }
    setIsSendingCode(true)
    setError(null)
    try {
      await seedingPublicApi.sendPhoneCode(stripPhone(phone))
      setCodeSent(true)
    } catch (err: any) {
      setError(err.message || '인증코드 발송에 실패했어요.')
    } finally {
      setIsSendingCode(false)
    }
  }

  // 알림톡 발송 + Lead 이벤트 (CAPI는 /v2/meta/capi/events로 직접 전송)
  const sendNotify = async () => {
    setIsSendingNotify(true)
    setError(null)
    try {
      const fbc = getCookie('_fbc')
      const fbp = getCookie('_fbp')
      const eventId = campaign.meta_pixel_id ? crypto.randomUUID() : undefined

      // 알림톡 발송 (fbc는 알림톡 링크에 보존용)
      await seedingPublicApi.notifySignup(mallId, shopNo, campaignId, stripPhone(phone), fbc)

      // 브라우저 Pixel + CAPI Lead 이벤트
      if (campaign.meta_pixel_id && eventId) {
        trackPixelEvent(
          campaign.meta_pixel_id,
          'Lead',
          {
            content_name: campaign.title,
            content_ids: [campaignId],
            content_category: 'seeding',
          },
          eventId,
        )
        // CAPI: /v2/meta/capi/events로 전송 (fire-and-forget)
        sendCapiEvent({
          mallId,
          shopNo,
          eventName: 'Lead',
          eventId,
          eventSourceUrl: window.location.href,
          userData: {
            ph: await hashPhone(stripPhone(phone)),
            fbp,
            fbc,
          },
          customData: { content_ids: [campaignId] },
        })
      }

      setNotifySent(true)
    } catch (err: any) {
      setError(err.message || '알림톡 발송에 실패했어요.')
    } finally {
      setIsSendingNotify(false)
    }
  }

  // 인증코드 확인 → 성공 시 알림톡 자동 발송
  const handleVerifyCode = async () => {
    if (!verificationCode) {
      setError('인증코드를 입력해주세요.')
      return
    }
    setIsVerifyingCode(true)
    setError(null)
    try {
      await seedingPublicApi.verifyPhoneCode(stripPhone(phone), verificationCode)
      setPhoneVerified(true)
      // 인증 완료 후 알림톡 자동 발송
      await sendNotify()
    } catch (err: any) {
      setError(err.message || '인증코드가 올바르지 않아요.')
    } finally {
      setIsVerifyingCode(false)
    }
  }

  return (
    <div className="mb-6">
      {/* 안내 메시지 */}
      <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg mb-4">
        <p className="text-primary font-medium mb-1">
          이 캠페인은 {campaign.shop_name || '쇼핑몰'} 회원만 신청할 수
          있습니다.
        </p>
        <p className="text-primary text-sm">
          전화번호를 입력하시면 회원가입 링크와 신청 절차를 알림톡으로 안내해
          드립니다.
        </p>
      </div>

      {!notifySent ? (
        <div className="space-y-3">
          {/* 전화번호 입력 + 인증 */}
          <div className="flex gap-2">
            <input
              type="tel"
              value={phone}
              onChange={(e) => {
                setPhone(formatPhoneInput(e.target.value, phone))
                if (phoneVerified) {
                  setPhoneVerified(false)
                  setCodeSent(false)
                  setVerificationCode('')
                }
              }}
              placeholder="010-1234-5678"
              className="flex-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-primary"
              readOnly={phoneVerified}
            />
            {!phoneVerified ? (
              <button
                type="button"
                onClick={handleSendCode}
                disabled={isSendingCode || !phone}
                className="px-3 py-2 bg-on-surface text-surface-lowest text-sm rounded-lg hover:bg-on-surface/90 disabled:bg-outline-variant disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSendingCode
                  ? '발송 중...'
                  : codeSent
                    ? '재발송'
                    : '인증요청'}
              </button>
            ) : (
              <span className="px-3 py-2 text-green-600 font-medium whitespace-nowrap">
                ✓ 인증완료
              </span>
            )}
          </div>

          {/* 인증코드 입력 */}
          {codeSent && !phoneVerified && (
            <div className="flex gap-2">
              <input
                type="text"
                value={verificationCode}
                onChange={(e) =>
                  setVerificationCode(
                    e.target.value.replace(/\D/g, '').slice(0, 6),
                  )
                }
                placeholder="6자리 인증코드"
                maxLength={6}
                className="flex-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-primary"
              />
              <button
                type="button"
                onClick={handleVerifyCode}
                disabled={
                  isVerifyingCode ||
                  isSendingNotify ||
                  verificationCode.length !== 6
                }
                className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary-dim disabled:bg-outline-variant disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isVerifyingCode || isSendingNotify ? '처리 중...' : '확인'}
              </button>
            </div>
          )}

          {/* 에러 */}
          {error && <p className="text-error text-sm">{error}</p>}
        </div>
      ) : (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
          <p className="text-green-700 font-medium mb-1">
            알림톡을 발송했어요.
          </p>
          <p className="text-green-600 text-sm">
            가입 후 다시 이 페이지에서 신청해주세요.
          </p>
        </div>
      )}

      {/* 이미 회원인 경우 로그인 */}
      <div className="mt-4 pt-4 border-t border-border text-center">
        <p className="text-sm text-on-surface-variant mb-2">이미 회원이신가요?</p>
        <button
          type="button"
          onClick={onLogin}
          className="px-6 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary-dim font-medium"
        >
          로그인하고 신청하기
        </button>
      </div>
    </div>
  )
}
